import { useState, useEffect, useRef } from "react";
import { ChevronsUpDown, Check, Plus } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const SHELL_CLASS =
    "inline-flex min-w-0 w-full items-center gap-1.5 rounded-md pl-1 pr-1.5 py-1 h-8 border border-transparent";

export function OrgSwitcher() {
    const { activeOrg, memberships, switchOrg } = useAuth();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", fn);
        return () => document.removeEventListener("mousedown", fn);
    }, []);

    if (!activeOrg) {
        return (
            <div className={`${SHELL_CLASS} hover:border-white/[0.06]`} aria-disabled data-testid="org-switcher-placeholder">
                <span className="h-5 w-5 rounded-md bg-[#262626] border border-white/10 flex items-center justify-center text-[9px] font-bold text-white/30 shrink-0">—</span>
                <span className="min-w-0 flex-1 truncate text-[13px] font-medium leading-none text-white/40">No workspace</span>
                <span className="hidden xl:flex fs-pill rounded-full bg-white/[0.04] px-1.5 py-0.5 text-[8px] font-semibold tracking-wider uppercase text-white/30 border border-white/10 w-11 justify-center shrink-0">—</span>
                <ChevronsUpDown className="h-3 w-3 text-white/20 shrink-0" />
            </div>
        );
    }

    const displayName = activeOrg?.name || "Unnamed organisation";
    const initials = displayName.trim().split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase();

    return (
        <div className="relative min-w-0 w-full h-8" ref={ref}>
            <button
                onClick={() => setOpen((v) => !v)}
                className={`${SHELL_CLASS} hover:bg-white/[0.04] hover:border-white/[0.06] transition-colors`}
                data-testid="org-switcher-button"
                title={displayName}
            >
                <span className="h-5 w-5 rounded-md bg-[#262626] border border-white/10 flex items-center justify-center text-[9px] font-bold text-[#00BF0D] shrink-0">
                    {initials}
                </span>
                <span className="min-w-0 flex-1 truncate text-left text-[13px] font-medium leading-none text-white">{displayName}</span>
                <span className="hidden xl:flex fs-pill rounded-full bg-white/[0.04] px-1.5 py-0.5 text-[8px] font-semibold tracking-wider uppercase text-white/60 border border-white/10 w-11 justify-center shrink-0">{activeOrg.role}</span>
                <ChevronsUpDown className="h-3 w-3 text-white/40 shrink-0" />
            </button>
            {open && (
                <div className="absolute left-0 mt-2 w-64 z-50 rounded-xl border border-white/[0.08] bg-[#1A1A1A] p-1.5 shadow-2xl" data-testid="org-switcher-menu">
                    <div className="px-2.5 pt-2 pb-1.5 text-[10px] font-semibold tracking-[0.15em] uppercase text-white/40">Workspaces</div>
                    {(memberships || []).map((m) => (
                        <button
                            key={m.id}
                            onClick={() => { switchOrg(m.id); setOpen(false); }}
                            className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-white/[0.05] flex items-center gap-2.5"
                            data-testid={`org-switch-${m.id}`}
                        >
                            <span className="h-7 w-7 rounded-md bg-[#262626] border border-white/10 flex items-center justify-center text-[11px] font-bold text-[#00BF0D]">
                                {(m.name || "?").trim().split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-white truncate">{m.name || "Unnamed organisation"}</p>
                                <p className="text-[11px] text-white/40 capitalize">{m.role} · {m.plan} plan</p>
                            </div>
                            {m.id === activeOrg.id && <Check className="h-4 w-4 text-[#00BF0D]" />}
                        </button>
                    ))}
                    <div className="border-t border-white/[0.06] mt-1.5 pt-1.5">
                        <button
                            className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-white/[0.05] flex items-center gap-2.5 text-sm text-white/65"
                            disabled
                            data-testid="org-create-disabled"
                            title="Multi-workspace creation coming soon"
                        >
                            <Plus className="h-4 w-4" />
                            <span>New workspace</span>
                            <span className="ml-auto text-[10px] uppercase tracking-wider text-white/30">Soon</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
